import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { useToast } from './ui/use-toast';
import { enrollInSchedule, updateEnrollment, checkAvailability, Schedule } from '@/api/scheduleApi';
import { Character } from '@/api/characterApi';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import ClassIconSimple from './ClassIconSimple';

interface ScheduleEnrollModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule: Schedule | null;
  schedules: Schedule[];
  characters: Character[];
  charactersLoading?: boolean;
  onSuccess: () => void;
  isUpdate?: boolean;
}

export default function ScheduleEnrollModal({ open, onOpenChange, schedule, schedules, characters, charactersLoading = false, onSuccess, isUpdate = false }: ScheduleEnrollModalProps) {
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);

  // 폼 데이터
  const [selectedCharacter, setSelectedCharacter] = useState('');
  const [atkPossible, setAtkPossible] = useState(false);
  const [supPossible, setSupPossible] = useState(false);

  // 모달 열릴 때 폼 초기화
  useEffect(() => {
    if (open) {
      setSelectedCharacter('');
      setAtkPossible(false);
      setSupPossible(false);
    }
  }, [open]);

  /**
   * 선택된 캐릭터가 레벨 부족인지 확인
   */
  const isLevelInsufficient = () => {
    if (!selectedCharacter || !schedule) return false;
    const character = characters.find((c) => c.characterNickname === selectedCharacter);
    if (!character) return false;
    const charLevel = parseFloat(character.itemLevel.replace(/,/g, ''));
    return charLevel < schedule.levelCut;
  };

  /**
   * 캐릭터가 같은 레이드에 이미 신청했는지 확인
   * @param characterNickname 캐릭터 닉네임
   */
  const isCharacterAlreadyEnrolled = (characterNickname: string): boolean => {
    if (!schedule) return false;

    // 현재 선택한 일정의 레이드 타이틀
    const currentRaidTitle = schedule.raidTitle;

    // 같은 레이드 타이틀을 가진 다른 일정 중에서 해당 캐릭터가 이미 신청한 것이 있는지 확인
    // isEnrolled === 1: 내가 참여 중인 일정
    // enrolledCharacterName: 참여한 캐릭터의 닉네임 (characterNickname)
    return schedules.some(s =>
      s.raidTitle === currentRaidTitle &&
      s.isEnrolled === 1 &&
      s.enrolledCharacterName === characterNickname &&
      s.scheduleId !== schedule.scheduleId // 현재 일정은 제외 (캐릭터 변경 모드 대비)
    );
  };

  /**
   * 캐릭터 선택 핸들러
   */
  const handleCharacterChange = (characterName: string) => {
    setSelectedCharacter(characterName);
    // 역할은 사용자가 직접 선택하도록 자동 체크 제거
  };

  /**
   * 폼 제출
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!schedule) return;

    // 유효성 검사
    if (!selectedCharacter) {
      toast({
        title: '오류',
        description: '캐릭터를 선택해주세요.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      // 수정 모드가 아닐 때만 참여 가능 여부 체크
      if (!isUpdate) {
        const availability = await checkAvailability(schedule.scheduleId, selectedCharacter);

        if (availability.isMemberApplied === 1) {
          toast({
            title: '참여 불가',
            description: '이미 이 일정에 참여 중입니다.',
            variant: 'destructive',
          });
          return;
        }

        if (availability.isExceeded === 1) {
          toast({
            title: '참여 불가',
            description: '인원이 마감되었습니다.',
            variant: 'destructive',
          });
          return;
        }
      }

      const requestData = {
        characterName: selectedCharacter,
        atkPossible: atkPossible ? 1 : 0,
        supPossible: supPossible ? 1 : 0,
        doubleType: '0000', // TODO: 이중 레이드 선택 UI 추가
      };

      if (isUpdate) {
        const result = await updateEnrollment(schedule.scheduleId, requestData);
        toast({
          title: '성공',
          description: result.message || '캐릭터가 변경되었습니다.',
        });
        onSuccess();
        onOpenChange(false);
      } else {
        const result = await enrollInSchedule(schedule.scheduleId, requestData);
        toast({
          title: '성공',
          description: result.message || '스케줄에 참여했습니다.',
        });
        onSuccess();
        onOpenChange(false);
      }
    } catch (error: any) {
      console.error('참여 실패:', error);

      const status = error?.response?.status;
      const serverMessage = error?.response?.data?.message;

      // HTTP 상태 코드로 에러 유형 구분
      if (status === 409) {
        // 409 Conflict: 동시성 제어 실패 (인원 초과 또는 중복 참여)
        toast({
          title: '참여 불가',
          description: serverMessage || '참여할 수 없습니다.',
          variant: 'destructive',
        });
      } else if (status === 500) {
        // 500 Internal Server Error: 서버 오류
        toast({
          title: '서버 오류',
          description: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
          variant: 'destructive',
        });
      } else {
        // 기타 에러 (네트워크 오류 등)
        toast({
          title: '오류',
          description: serverMessage || '스케줄 참여에 실패했습니다.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>스케줄 참여</DialogTitle>
          <DialogDescription>
            {schedule && `${schedule.raidTitle} 일정에 참여합니다`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 스케줄 정보 */}
          {schedule && (
            <div className="bg-accent/20 rounded-lg p-3 space-y-1 border border-accent">
              <p className="text-sm font-semibold">{schedule.raidTitle}</p>
              <p className="text-xs text-muted-foreground">
                입장 레벨: {schedule.levelCut} 이상
              </p>
              <p className="text-xs text-muted-foreground">
                현재 참여: {schedule.enrollMemberNum}/{schedule.atkNum + schedule.supNum}명
              </p>
            </div>
          )}

          {/* 캐릭터 선택 */}
          <div className="space-y-2">
            <Label htmlFor="character">캐릭터 선택 *</Label>
            {charactersLoading ? (
              <p className="text-sm text-muted-foreground">
                캐릭터 목록을 불러오는 중...
              </p>
            ) : characters.length === 0 ? (
              <p className="text-sm text-destructive">
                등록된 캐릭터가 없습니다. 먼저 캐릭터를 등록해주세요.
              </p>
            ) : (
              <>
                <Select value={selectedCharacter} onValueChange={handleCharacterChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="캐릭터를 선택하세요" />
                  </SelectTrigger>
                  <SelectContent>
                    {characters.map((char) => {
                      const charLevel = parseFloat(char.itemLevel.replace(/,/g, ''));
                      const isLowLevel = schedule && charLevel < schedule.levelCut;
                      const isDuplicate = isCharacterAlreadyEnrolled(char.characterNickname);
                      return (
                        <SelectItem
                          key={char.characterNickname}
                          value={char.characterNickname}
                          className={isDuplicate ? 'bg-orange-50 dark:bg-orange-950/30' : ''}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                              <ClassIconSimple
                                characterClass={char.characterClass}
                                classType={char.classType as 'attacker' | 'supporter'}
                                size={24}
                              />
                              <span>{char.characterNickname}</span>
                              <span className="text-xs text-muted-foreground">Lv.{char.itemLevel}</span>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              {isLowLevel && <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-500" />}
                              {isDuplicate && <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-500" />}
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>

                {/* 레벨 부족 경고 */}
                {isLevelInsufficient() && (
                  <div className="flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900/50 rounded-md">
                    <span className="text-yellow-600 dark:text-yellow-500">⚠️</span>
                    <p className="text-xs text-yellow-800 dark:text-yellow-300">
                      입장 레벨 미달 (신청 가능)
                    </p>
                  </div>
                )}

                {/* 중복 참여 경고 */}
                {selectedCharacter && (() => {
                  const selectedChar = characters.find(c => c.characterNickname === selectedCharacter);
                  if (!selectedChar || !schedule) return null;

                  // 중복된 일정 찾기
                  const duplicateSchedule = schedules.find(s =>
                    s.raidTitle === schedule.raidTitle &&
                    s.isEnrolled === 1 &&
                    s.enrolledCharacterName === selectedChar.characterNickname &&
                    s.scheduleId !== schedule.scheduleId
                  );

                  if (!duplicateSchedule) return null;

                  // 날짜/시간 포맷팅
                  const formatDateTime = (datetime: string) => {
                    const date = new Date(datetime);
                    const month = date.getMonth() + 1;
                    const day = date.getDate();
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    return `${month}/${day} ${hours}:${minutes}`;
                  };

                  return (
                    <div className="flex items-start gap-2 p-3 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900/50 rounded-md">
                      <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-orange-800 dark:text-orange-300 mb-1">
                          중복 참여 감지
                        </p>
                        <p className="text-xs text-orange-700 dark:text-orange-400">
                          이미 "{duplicateSchedule.raidTitle}" ({formatDateTime(duplicateSchedule.startTime)})에 참여 중입니다
                        </p>
                        <p className="text-xs text-orange-600 dark:text-orange-500 mt-1">
                          * 신청은 가능합니다
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>

          {/* 역할 선택 */}
          {selectedCharacter && (() => {
            const character = characters.find((c) => c.characterNickname === selectedCharacter);
            if (!character) return null;

            return (
              <div className="space-y-2">
                <Label>참여 역할 (선택)</Label>
                <div className="space-y-2">
                  {character.classType === 'attacker' && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="supPossible"
                        checked={supPossible}
                        onCheckedChange={(checked) => setSupPossible(checked as boolean)}
                      />
                      <Label htmlFor="supPossible" className="text-sm font-normal cursor-pointer">
                        서폿도 가능
                      </Label>
                    </div>
                  )}
                  {character.classType === 'supporter' && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="atkPossible"
                        checked={atkPossible}
                        onCheckedChange={(checked) => setAtkPossible(checked as boolean)}
                      />
                      <Label htmlFor="atkPossible" className="text-sm font-normal cursor-pointer">
                        딜러도 가능
                      </Label>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              취소
            </Button>
            <Button type="submit" disabled={loading || charactersLoading || characters.length === 0}>
              {loading ? '참여 중...' : '참여하기'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
